import React from 'react';
import { agGridDataFormatter, shouldRenderAsHtml } from './helpers/TableColumnFormatting';
import { getFieldName } from './helpers/getFieldName';
import { ICellRendererParams } from '@ag-grid-community/core';
import { CustomAgGridContext } from 'common/types/agGrid/context';
import CustomLinkRenderer from './CustomLinkRenderer';
import { defaultLinkedAction, defaultProcessDiscoveryContext } from './helpers/processDiscoveryHelper';

export interface IValueRendererProps extends ICellRendererParams<any, any, Partial<CustomAgGridContext>> {
  domain: string;
  datasetUid: string;
  wrapWithDivElement: boolean;
  forExport?: boolean;
}

export const ValueRenderer = (params: IValueRendererProps) => {
  const ctx = params.context;
  const columnMetadata = ctx.columnMetadata ?? [];
  const columnFormats = ctx.columnFormats ?? {};
  const nonStandardAggregations = ctx.nonStandardAggregations ?? [];
  const linkedAction = ctx.linkedAction ?? defaultLinkedAction;
  const processDiscoveryContext = ctx.processDiscoveryContext ?? defaultProcessDiscoveryContext;
  const fieldName = getFieldName(params);

  const renderTextAsHtml = (text: string) => {
    return <div dangerouslySetInnerHTML={{ __html: text }}></div>;
  };

  const formattedValue = agGridDataFormatter(
    params.domain,
    params.datasetUid,
    columnMetadata,
    nonStandardAggregations,
    params,
    columnFormats,
  );

  // Currently, we do not need to export values enclosed in div elements in the Excel export.
  if (params.forExport) {
    return formattedValue;
  }

  if (linkedAction?.selectedExistingColumn && fieldName === linkedAction?.selectedExistingColumn) {
    const primaryKeyColumn = processDiscoveryContext.primaryKeyColumn;
    const primaryColumnValue = params.data[primaryKeyColumn];
    const selectedActionForExistingColumn = linkedAction.selectedActionForExistingColumn;

    // Don't render link if no primary key value - just show plain text
    if (!primaryColumnValue) {
      return formattedValue;
    }

    return (
      <CustomLinkRenderer
        formattedValue={formattedValue}
        primaryColumnValue={primaryColumnValue}
        processDiscoveryContext={processDiscoveryContext}
        selectedActionForExistingColumn={selectedActionForExistingColumn}
      />
    );
  }

  return params.wrapWithDivElement || shouldRenderAsHtml(params.node, columnMetadata)
    ? renderTextAsHtml(formattedValue)
    : formattedValue;
};
